import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiEndpointsService } from './api-endpoints.service';
import { ApiHttpService } from './api-http.service';

export interface TenantThemeDto {
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  background?: {
    color?: string | null;
    gradient?: Array<string | null> | null;
  } | null;
  config?: Record<string, unknown> | null;
}

export interface TenantThemeFallback {
  primaryColor?: string | null;
  primaryLightColor?: string | null;
  secondaryColor?: string | null;
  secondaryLightColor?: string | null;
}

export interface TenantBrandingState {
  tenantId: string | null;
  theme: TenantThemeDto | null;
  isLoading: boolean;
}

const DEFAULT_THEME = {
  primaryColor: '#003A29',
  primaryLightColor: '#f4faea',
  secondaryColor: '#ffe058',
  secondaryLightColor: '#f8ea60',
  accentColor: '#f2672d',
  accentSoftColor: '#fff2eb',
  accentBorderColor: '#ffe3d5',
  accentTintColor: '#fffaf7',
  accentStrongColor: '#e4511a',
} as const;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

@Injectable({
  providedIn: 'root',
})
export class TenantBrandingService {
  private readonly cache = new Map<string, TenantThemeDto>();
  private readonly inFlight = new Map<string, Observable<TenantThemeDto | null>>();
  private activeTenantId: string | null = null;
  private readonly stateSubject = new BehaviorSubject<TenantBrandingState>({
    tenantId: null,
    theme: null,
    isLoading: false,
  });

  readonly state$ = this.stateSubject.asObservable();

  constructor(
    private readonly apiHttpService: ApiHttpService,
    private readonly apiEndpointsService: ApiEndpointsService
  ) {}

  loadForTenant(
    tenantId: string | null | undefined,
    fallback: TenantThemeFallback = {}
  ): Observable<TenantThemeDto | null> {
    const normalizedTenantId = tenantId?.trim() || null;
    this.activeTenantId = normalizedTenantId;
    this.applyFallback(fallback);

    if (!normalizedTenantId || !environment.teamApiUrl?.trim()) {
      this.reset(normalizedTenantId);
      return of(null);
    }

    const cachedTheme = this.cache.get(normalizedTenantId);
    if (cachedTheme) {
      this.applyTheme(normalizedTenantId, cachedTheme);
      return of(cachedTheme);
    }

    const existingRequest = this.inFlight.get(normalizedTenantId);
    if (existingRequest) {
      return existingRequest;
    }

    this.stateSubject.next({
      tenantId: normalizedTenantId,
      theme: null,
      isLoading: true,
    });

    const request$ = this.apiHttpService
      .get(this.apiEndpointsService.getTenantTheme(normalizedTenantId))
      .pipe(
        map(response => this.unwrapTheme(response)),
        tap(theme => {
          if (theme) {
            this.cache.set(normalizedTenantId, theme);
            this.applyTheme(normalizedTenantId, theme);
          } else if (this.activeTenantId === normalizedTenantId) {
            this.reset(normalizedTenantId);
          }
        }),
        catchError(() => {
          if (this.activeTenantId === normalizedTenantId) {
            this.reset(normalizedTenantId);
          }
          return of(null);
        }),
        finalize(() => this.inFlight.delete(normalizedTenantId)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.inFlight.set(normalizedTenantId, request$);
    return request$;
  }

  reset(tenantId: string | null = null): void {
    this.activeTenantId = tenantId;
    this.cache.clear();
    this.clearCssVariables();
    this.stateSubject.next({ tenantId, theme: null, isLoading: false });
  }

  private unwrapTheme(response: unknown): TenantThemeDto | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const envelope = response as { data?: unknown };
    const value = envelope.data ?? response;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    return value as TenantThemeDto;
  }

  private applyTheme(tenantId: string, theme: TenantThemeDto): void {
    if (this.activeTenantId !== tenantId) {
      return;
    }
    const gradient = theme.background?.gradient ?? [];
    const primaryColor = this.colorOrDefault(
      theme.primaryColor,
      this.colorOrDefault(theme.background?.color, DEFAULT_THEME.primaryColor)
    );
    const secondaryColor = this.colorOrDefault(
      theme.secondaryColor,
      this.colorOrDefault(gradient[1], DEFAULT_THEME.secondaryColor)
    );
    const accentColor = this.colorOrDefault(
      theme.accentColor,
      this.colorOrDefault(gradient[2], secondaryColor)
    );
    const config = theme.config ?? {};
    const primaryLightColor = this.colorOrDefault(
      config['primaryLightColor'],
      DEFAULT_THEME.primaryLightColor
    );
    const secondaryLightColor = this.colorOrDefault(
      config['secondaryLightColor'],
      DEFAULT_THEME.secondaryLightColor
    );

    this.setCssVariable('--primary-color', primaryColor);
    this.setCssVariable('--active-bg-color', primaryColor);
    this.setCssVariable('--primary-light-color', primaryLightColor);
    this.setCssVariable('--secondary-color', secondaryColor);
    this.setCssVariable('--secondary-light-color', secondaryLightColor);
    this.setCssVariable('--accent-color', accentColor);
    this.applyAccentVariants(accentColor);
    this.stateSubject.next({ tenantId, theme, isLoading: false });
  }

  private applyFallback(fallback: TenantThemeFallback): void {
    this.setCssVariable(
      '--primary-color',
      this.colorOrDefault(fallback.primaryColor, DEFAULT_THEME.primaryColor)
    );
    this.setCssVariable(
      '--active-bg-color',
      this.colorOrDefault(fallback.primaryColor, DEFAULT_THEME.primaryColor)
    );
    this.setCssVariable(
      '--primary-light-color',
      this.colorOrDefault(
        fallback.primaryLightColor,
        DEFAULT_THEME.primaryLightColor
      )
    );
    this.setCssVariable(
      '--secondary-color',
      this.colorOrDefault(fallback.secondaryColor, DEFAULT_THEME.secondaryColor)
    );
    this.setCssVariable(
      '--secondary-light-color',
      this.colorOrDefault(
        fallback.secondaryLightColor,
        DEFAULT_THEME.secondaryLightColor
      )
    );
    this.setCssVariable('--accent-color', DEFAULT_THEME.accentColor);
    this.setCssVariable('--accent-soft-color', DEFAULT_THEME.accentSoftColor);
    this.setCssVariable(
      '--accent-border-color',
      DEFAULT_THEME.accentBorderColor
    );
    this.setCssVariable('--accent-tint-color', DEFAULT_THEME.accentTintColor);
    this.setCssVariable(
      '--accent-strong-color',
      DEFAULT_THEME.accentStrongColor
    );
  }

  private clearCssVariables(): void {
    this.applyFallback({});
  }

  private applyAccentVariants(accentColor: string): void {
    this.setCssVariable('--accent-soft-color', this.blendWithWhite(accentColor, 0.9));
    this.setCssVariable('--accent-border-color', this.blendWithWhite(accentColor, 0.78));
    this.setCssVariable('--accent-tint-color', this.blendWithWhite(accentColor, 0.96));
    this.setCssVariable('--accent-strong-color', this.blendWithBlack(accentColor, 0.2));
  }

  private blendWithWhite(color: string, whiteRatio: number): string {
    return this.blend(color, [255, 255, 255], whiteRatio);
  }

  private blendWithBlack(color: string, blackRatio: number): string {
    return this.blend(color, [0, 0, 0], blackRatio);
  }

  private blend(color: string, target: [number, number, number], ratio: number): string {
    const hex = color.slice(1);
    const expanded = hex.length === 3 || hex.length === 4
      ? hex.slice(0, 3).split('').map(channel => channel + channel).join('')
      : hex.slice(0, 6);
    const channels = [0, 2, 4].map(offset => Number.parseInt(expanded.slice(offset, offset + 2), 16));
    if (channels.some(channel => !Number.isFinite(channel))) {
      return color;
    }
    const blended = channels.map((channel, index) =>
      Math.round(channel + (target[index] - channel) * ratio)
    );
    return `#${blended.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
  }

  private setCssVariable(name: string, value: string): void {
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty(name, value);
    }
  }

  private validColor(value: unknown): value is string {
    return typeof value === 'string' && HEX_COLOR.test(value.trim());
  }

  private colorOrDefault(value: unknown, fallback: string): string {
    return this.validColor(value) ? value.trim() : fallback;
  }
}
